<template>
  <div
    v-if="$vuetify.breakpoint.xs"
    class="compact-header"
    :class="{
      'dark-theme': $vuetify.theme.dark,
      'light-theme': !$vuetify.theme.dark,
      'video-mode': isVideoMode
    }"
  >
    <!-- LEFT SIDE: Toggles (Audio Mode Only) -->
    <div v-if="!isVideoMode" class="header-left">
      <div class="controls-toggles">
        <!-- HI-FI SWITCH -->
        <div class="toggle-group">
          <span class="toggle-label">hi-fi:</span>
          <v-switch
            v-model="highQualityAudioMode"
            hide-details
            dense
            class="compact-switch"
          ></v-switch>
        </div>

        <!-- PLAYLIST SWITCH (if logged in and on master playlist) -->
        <div v-if="isLoggedIn && (!playlistID && !isPollResultsMode)" class="toggle-group">
          <span class="toggle-label">playlist:</span>
          <v-switch
            v-model="createPlaylistMode"
            hide-details
            dense
            class="compact-switch"
          ></v-switch>
        </div>

        <!-- POLL SWITCH (if logged in and on master playlist) -->
        <div v-if="isLoggedIn && (!playlistID && !isPollResultsMode)" class="toggle-group">
          <span class="toggle-label">poll:</span>
          <v-switch
            v-model="pollMode"
            hide-details
            dense
            class="compact-switch"
          ></v-switch>
        </div>
      </div>
    </div>

    <!-- CENTER: Logo (Audio Mode Only) -->
    <div v-if="!isVideoMode" class="header-center">
      <img
        :src="logo"
        alt="PCP"
        class="compact-logo"
      />
    </div>

    <!-- RIGHT SIDE: Control Buttons (2 rows) -->
    <div class="header-right" v-show="!isVideoFullscreen">
      <!-- Row 1: Sort, Search, Video Mode -->
      <div class="controls-buttons-row">
        <!-- Sorting is offered to everyone (even logged out, on any playlist)
             once the list has enough tracks — see ui/shouldShowSortOptions. -->
        <v-btn
          v-if="shouldShowSortOptions"
          icon
          x-small
          @click="toggleSort"
          class="header-button"
        >
          <v-icon small>{{ sortOrder === 'chronologically' ? 'mdi-sort-alphabetical-descending-variant' : 'mdi-sort-calendar-ascending' }}</v-icon>
        </v-btn>

        <v-btn
          v-if="isLoggedIn && (!playlistID && !isPollResultsMode)"
          icon
          x-small
          @click="toggleSearchBar"
          class="header-button"
        >
          <v-icon small>mdi-magnify</v-icon>
        </v-btn>

        <v-btn
          v-if="isLoggedIn && (!playlistID && !isPollResultsMode)"
          icon
          x-small
          @click="toggleVideoMode"
          class="header-button"
          :class="{ 'active-button': isVideoMode }"
        >
          <v-icon small>mdi-video</v-icon>
        </v-btn>
      </div>

      <!-- Row 2: Theme, Upload, Login/Logout -->
      <div class="controls-buttons-row">
        <v-btn
          icon
          x-small
          @click="toggleDarkMode"
          class="header-button"
        >
          <v-icon small>{{ $vuetify.theme.dark ? 'mdi-white-balance-sunny' : 'mdi-moon-waxing-crescent' }}</v-icon>
        </v-btn>

        <v-btn
          v-if="isLoggedIn && isSuperAdmin"
          icon
          x-small
          @click="showUploadDialog"
          class="header-button"
        >
          <v-icon small>mdi-plus</v-icon>
        </v-btn>

        <v-btn
          icon
          x-small
          @click="isLoggedIn ? logout() : showLoginDialog()"
          class="header-button"
        >
          <v-icon small>{{ isLoggedIn ? 'mdi-logout' : 'mdi-lock-outline' }}</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import VideoService from '@/services/VideoService';

export default {
  name: 'CompactHeader',

  data() {
    return {
      isVideoFullscreen: false,
    };
  },

  computed: {
    ...mapGetters({
      isLoggedIn: 'auth/isLoggedIn',
      isSuperAdmin: 'auth/isSuperAdmin',
      playlistID: 'playlist/playlistID',
      isVideoMode: 'ui/isVideoMode',
      sortOrder: 'ui/sortOrder',
      isPollResultsMode: 'ui/isPollResultsMode',
      shouldShowSortOptions: 'ui/shouldShowSortOptions',
    }),

    logo() {
      return this.$vuetify.theme.dark
        ? require('../../assets/logo dark theme.png')
        : require('../../assets/logo.png');
    },

    highQualityAudioMode: {
      get() {
        return this.$store.getters['ui/isHighQualityAudioMode'];
      },
      set(value) {
        this.$store.dispatch('ui/setHighQualityAudioMode', value);
      }
    },

    createPlaylistMode: {
      get() {
        return this.$store.getters['ui/isCreatePlaylistMode'];
      },
      set(value) {
        this.$store.dispatch('ui/setCreatePlaylistMode', value);
      }
    },

    pollMode: {
      get() {
        return this.$store.getters['ui/isPollMode'];
      },
      set(value) {
        this.$store.dispatch('ui/setPollMode', value);
        if (value) {
          this.$store.dispatch('poll/enterPollMode');
        }
      }
    },
  },

  mounted() {
    // Set up fullscreen change listener
    VideoService.setFullscreenChangeCallback((isFullscreen) => {
      this.isVideoFullscreen = isFullscreen;
    });
  },

  beforeDestroy() {
    // Clean up listener
    VideoService.setFullscreenChangeCallback(null);
  },

  methods: {
    ...mapActions({
      logout: 'auth/logout',
      toggleSearchBarAction: 'ui/toggleSearchBar',
      setSortOrder: 'ui/setSortOrder',
    }),

    toggleSort() {
      const newOrder = this.sortOrder === 'chronologically' ? 'alphabetically' : 'chronologically';
      this.setSortOrder(newOrder);
    },

    toggleSearchBar() {
      this.toggleSearchBarAction();
    },

    toggleVideoMode() {
      this.$store.dispatch('ui/toggleVideoMode');
    },

    toggleDarkMode() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
    },

    showUploadDialog() {
      this.$emit('showUpload');
    },

    showLoginDialog() {
      this.$emit('showLogin');
    },
  },
};
</script>

<style scoped>
.compact-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: start;
  padding: 6px 8px 6px 4px;
  min-height: 70px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Video mode: single column for buttons only */
.compact-header.video-mode {
  grid-template-columns: 1fr;
  justify-items: flex-end;
  min-height: 36px;  /* Reduced for single row */
  padding: 4px 8px;   /* Tighter vertical padding */
}

.compact-header.video-mode .header-left,
.compact-header.video-mode .header-center {
  display: none;
}

/* Video mode: buttons in single row */
.compact-header.video-mode .header-right {
  flex-direction: row;  /* Single row instead of column */
  gap: 2px;
}

.compact-header.video-mode .controls-buttons-row {
  gap: 2px;  /* Keep button spacing consistent */
}

.dark-theme.compact-header {
  background: rgba(18, 18, 18, 0.98);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.light-theme.compact-header {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.header-left {
  justify-self: start;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 10px;
}

.header-center {
  justify-self: center;
}

.compact-logo {
  width: 120px;
  height: auto;
  max-height: 90px;
  object-fit: contain;
  display: block;
}

.header-right {
  justify-self: end;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.controls-buttons-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 2px;
  justify-content: flex-end;
}

.controls-toggles {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toggle-group {
  display: flex;
  align-items: center;
  gap: 2px;
}

.toggle-label {
  font-size: 10px;
  white-space: nowrap;
  opacity: 0.8;
  min-width: 42px;
  text-align: right;
}

.dark-theme .toggle-label {
  color: white;
}

.light-theme .toggle-label {
  color: black;
}

.compact-switch {
  margin: 0 !important;
  padding: 0 !important;
}

.compact-switch >>> .v-input__slot {
  margin: 0 !important;
}

.compact-switch >>> .v-input--switch__track {
  height: 12px;
  width: 24px;
}

.compact-switch >>> .v-input--switch__thumb {
  height: 16px;
  width: 16px;
}

.header-button {
  min-width: 28px !important;
  min-height: 28px !important;
  width: 28px !important;
  height: 28px !important;
}

/* Active button styling for better contrast (video mode button) */
.dark-theme .active-button {
  background-color: #e0e0e0 !important;
}

.dark-theme .active-button .v-icon {
  color: #212121 !important;
}

.light-theme .active-button {
  background-color: #424242 !important;
}

.light-theme .active-button .v-icon {
  color: #e0e0e0 !important;
}

</style>
